import React, { useState } from "react";

// ─── Styles ────────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 1400,
    background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  modal: {
    background: "#fff", borderRadius: 14,
    width: "min(780px, 95vw)", maxHeight: "88vh",
    display: "flex", flexDirection: "column",
    boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
    fontFamily: "'Noto Sans Thai','DM Sans',sans-serif",
  },
  modalHeader: {
    padding: "16px 22px", borderBottom: "1px solid #f1f5f9",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexShrink: 0,
  },
  closeBtn: {
    background: "none", border: "none", fontSize: 20,
    cursor: "pointer", color: "#94a3b8", lineHeight: 1,
    padding: "2px 6px", borderRadius: 6,
  },
  body: { overflowY: "auto", padding: "16px 22px 22px", flex: 1 },
  section: {
    marginBottom: 20, borderRadius: 10,
    border: "1px solid #e2e8f0", overflow: "hidden",
  },
  sectionHeader: {
    padding: "10px 16px", display: "flex", alignItems: "center",
    gap: 10, cursor: "pointer", userSelect: "none",
    background: "#f8fafc",
  },
  sectionTitle: { fontWeight: 700, fontSize: 13, color: "#1e293b", flex: 1 },
  sectionBody: { padding: "12px 16px", background: "#fff" },
  step: {
    display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start",
  },
  stepNum: {
    minWidth: 22, height: 22, borderRadius: "50%",
    background: "#6366f1", color: "#fff",
    fontSize: 11, fontWeight: 800,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  stepText: { fontSize: 13, color: "#334155", lineHeight: 1.6 },
  badge: (color) => ({
    display: "inline-block", borderRadius: 4,
    padding: "1px 8px", fontSize: 11, fontWeight: 800,
    background: color + "22", color: color,
    border: `1px solid ${color}55`, marginRight: 4,
  }),
  note: {
    background: "#fffbeb", border: "1px solid #fde68a",
    borderRadius: 6, padding: "8px 12px",
    fontSize: 12, color: "#92400e", marginTop: 8,
  },
  tip: {
    background: "#f0fdf4", border: "1px solid #86efac",
    borderRadius: 6, padding: "8px 12px",
    fontSize: 12, color: "#166534", marginTop: 8,
  },
};

// ─── Guide Content ─────────────────────────────────────────────────────────────
function Section({ icon, title, color = "#6366f1", children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={S.section}>
      <div style={{ ...S.sectionHeader, borderLeft: `4px solid ${color}` }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={S.sectionTitle}>{title}</span>
        <span style={{ color: "#94a3b8", fontSize: 14 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={S.sectionBody}>{children}</div>}
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div style={S.step}>
      <div style={S.stepNum}>{n}</div>
      <div style={S.stepText}>{children}</div>
    </div>
  );
}

function GuideContent() {
  return (
    <div style={S.body}>

      {/* ── ภาพรวม ── */}
      <Section icon="🎯" title="ภาพรวมระบบ" color="#6366f1">
        <p style={{ fontSize: 13, color: "#475569", marginBottom: 10, lineHeight: 1.7 }}>
          ระบบนี้ใช้สำหรับ <strong>ตรวจสอบและเปรียบเทียบอัตราค่าระวาง Ocean Freight</strong> เส้นทางไทย–สหรัฐอเมริกา
          เพื่อนำไปยื่น FMC Rate Filing โดยดึงข้อมูลจาก AR ที่ผ่านการอนุมัติ (SAP_ACCEPTED) ประจำเดือนปัจจุบัน
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            ["🔍", "เปรียบเทียบ rate", "#6366f1"],
            ["💾", "บันทึก Snapshot", "#16a34a"],
            ["📋", "ดูประวัติ", "#6366f1"],
            ["📤", "Update Snapshot", "#d97706"],
            ["📝", "Update Log", "#7c3aed"],
          ].map(([icon, label, color]) => (
            <span key={label} style={{ background: color + "11", color, border: `1px solid ${color}33`, borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700 }}>
              {icon} {label}
            </span>
          ))}
        </div>
      </Section>

      {/* ── ปุ่มทั่วไป ── */}
      {/* <Section icon="🖱️" title="ปุ่มทั่วไป" color="#475569">
        <Step n="1"><strong>Search</strong> — ดึงข้อมูล Ocean Freight</Step>
        <Step n="2"><strong>Clear Filter</strong> — รีเซ็ต filter ทั้งหมด</Step>
        <Step n="3"><strong>Refresh data</strong> — โหลดข้อมูลใหม่ใน Data Table</Step>
        <Step n="4"><strong>Reload page</strong> — รีโหลดหน้าเว็บทั้งหมด (ใช้เมื่อมีปัญหาการแสดงผล)</Step>
      </Section> */}

      {/* ── เปรียบเทียบ Snapshot ── */}
      <Section icon="🔍" title="เปรียบเทียบกับ Snapshot" color="#6366f1">
        <Step n="1">กด <strong style={{ color: "#6366f1" }}>🔍 เปรียบเทียบกับ Snapshot</strong> เพื่อเทียบข้อมูลปัจจุบันกับ snapshot ล่าสุด</Step>
        <Step n="2">ระบบจะแสดง Summary Bar พร้อม status แต่ละประเภท:</Step>
        <div style={{ marginLeft: 32, marginBottom: 8 }}>
          <div><span style={S.badge("#16a34a")}>NEW</span> — rate ใหม่ที่ยังไม่เคย file</div>
          <div><span style={S.badge("#d97706")}>CHANGED</span> — rate เดิมที่มีการเปลี่ยนแปลง (hover เซลสีเหลืองเพื่อดูค่าเดิม)</div>
          <div><span style={S.badge("#dc2626")}>REMOVED</span> — rate ที่หายไปจากเดือนนี้</div>
          <div><span style={S.badge("#94a3b8")}>SAME</span> — rate ที่ไม่เปลี่ยนแปลง</div>
        </div>
        <Step n="3">คลิกที่ badge แต่ละอันเพื่อ filter ดูเฉพาะ status นั้น คลิกซ้ำเพื่อยกเลิก filter</Step>
        <div style={S.note}>💡 หลังกดเปรียบเทียบ ระบบจะ filter แสดง NEW อัตโนมัติ</div>
      </Section>

      {/* ── บันทึก Snapshot ── */}
      <Section icon="💾" title="บันทึกเป็น Snapshot ใหม่" color="#16a34a">
        <Step n="1">กด <strong style={{ color: "#16a34a" }}>💾 บันทึกเป็น Snapshot ใหม่</strong> เพื่อบันทึกข้อมูล rate เดือนปัจจุบัน</Step>
        <Step n="2">ระบบจะบันทึกลง Database และ <strong>Export Excel อัตโนมัติ</strong></Step>
        <Step n="3">ไฟล์ Excel ที่ได้จะมี <strong>Rate ID (hidden)</strong> อยู่ด้วย ใช้สำหรับ Update Snapshot ได้เลย</Step>
        <div style={S.tip}>✅ Excel ที่ได้จากปุ่มนี้สามารถนำไป Update Snapshot ได้ทันที ไม่ต้อง Export ซ้ำจากประวัติ</div>
      </Section>

      {/* ── ประวัติ Snapshot ── */}
      <Section icon="📋" title="ประวัติ Snapshot" color="#6366f1">
        <Step n="1">กด <strong style={{ color: "#6366f1" }}>📋 ประวัติ Snapshot</strong> เพื่อดูรายการ snapshot ทั้งหมดที่เคยบันทึก</Step>
        <Step n="2">กด <strong>📥 Export</strong> หน้า snapshot ที่ต้องการ เพื่อดาวน์โหลด Excel พร้อม Rate ID</Step>
        <Step n="3">ไฟล์ที่ Export ออกมาจากหน้านี้สามารถนำไปใช้ <strong>Update Snapshot</strong> ได้เช่นกัน</Step>
        <div style={S.note}>💡 ใช้เมื่อต้องการดึง snapshot เก่าออกมาแก้ไข</div>
      </Section>

      {/* ── Update Snapshot ── */}
      <Section icon="📤" title="Update Snapshot" color="#d97706">
        <Step n="1">กด <strong style={{ color: "#d97706" }}>📤 Update Snapshot</strong> เพื่อแก้ไขข้อมูลใน snapshot ที่บันทึกไว้</Step>
        <Step n="2">ระบบจะ <strong>auto select snapshot ล่าสุด</strong> ให้อัตโนมัติ สามารถเปลี่ยน snapshot ได้จาก dropdown</Step>
        <Step n="3">เปิดไฟล์ Excel ที่ได้จากปุ่ม <strong>บันทึก Snapshot</strong> หรือ <strong>Export</strong> จากประวัติ แล้วแก้ไขข้อมูลที่ต้องการ</Step>
        <Step n="4">Upload ไฟล์ Excel กลับเข้าระบบ กด <strong>📤 Update Snapshot</strong></Step>
        <Step n="5">ระบบจะ update เฉพาะ row ที่มีการเปลี่ยนแปลง และแสดงผลสรุป: แก้ไข / ไม่เปลี่ยน / ไม่พบ</Step>
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Field ที่แก้ไขได้:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {["Commodity", "HS Code", "Port of Loading", "Port of Discharge",
             , "Destination", "Services Type", "Rate Base", "Selling Rate"].map(f => (
              <span key={f} style={{ background: "#f1f5f9", borderRadius: 4, padding: "2px 8px", fontSize: 11, color: "#475569" }}>{f}</span>
            ))}
          </div>
        </div>
        <div style={S.note}>
          ⚠️ ใช้เฉพาะไฟล์ที่ Export จาก <strong>บันทึก Snapshot</strong> หรือ <strong>ประวัติ Snapshot</strong> เท่านั้น
          เนื่องจากไฟล์ต้องมี Rate ID สำหรับอ้างอิง
        </div>
      </Section>

      {/* ── Update Log ── */}
      <Section icon="📝" title="View Log — ประวัติการแก้ไข" color="#7c3aed">
        <Step n="1">กด <strong style={{ color: "#7c3aed" }}>📝 View Log</strong> เพื่อดูประวัติการ update snapshot ทั้งหมด</Step>
        <Step n="2">กรอง snapshot ที่ต้องการดูได้จาก <strong>dropdown "กรอง Snapshot"</strong></Step>
        <Step n="3">กด <strong>▸</strong> หน้าแต่ละรายการเพื่อ <strong>expand ดูรายละเอียด</strong> ว่า field ไหนถูกเปลี่ยนแปลง</Step>
        <div style={{ marginLeft: 32, marginTop: 4, marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>ข้อมูลที่แสดงในแต่ละรายการ:</div>
          <div style={{ fontSize: 12, color: "#475569" }}>• Snapshot ที่ถูก update</div>
          <div style={{ fontSize: 12, color: "#475569" }}>• ชื่อไฟล์ที่ Upload</div>
          <div style={{ fontSize: 12, color: "#475569" }}>• จำนวน row ที่ <span style={{ color: "#16a34a", fontWeight: 700 }}>แก้ไข</span> / ไม่เปลี่ยน / ไม่พบ</div>
          <div style={{ fontSize: 12, color: "#475569" }}>• ผู้ที่ทำการ update และวันที่</div>
        </div>
        <Step n="4">เมื่อ expand จะเห็น <strong>Rate ID, Field, ค่าเดิม (แดง), ค่าใหม่ (เขียว)</strong> ของทุก field ที่เปลี่ยน</Step>
        <div style={S.tip}>✅ ใช้ตรวจสอบว่ามีการแก้ไขข้อมูลอะไรไปบ้าง และแก้ไขเมื่อไหร่</div>
      </Section>

      {/* ── Export DataGrid ── */}
      <Section icon="📊" title="Export ข้อมูลจาก DataGrid" color="#0891b2">
        <Step n="1">กดปุ่ม Export ที่มุมขวาบนของ DataGrid เพื่อ download ข้อมูลที่แสดงอยู่เป็น Excel</Step>
        <Step n="2">ถ้าอยู่ใน <strong>Diff Mode</strong> จะ export เฉพาะ row ที่ filter อยู่ (เช่น เฉพาะ NEW หรือ CHANGED)</Step>
        <div style={S.note}>💡 Export นี้ไม่มี Rate ID — ใช้สำหรับดูข้อมูลเท่านั้น ไม่สามารถนำไป Update Snapshot ได้</div>
      </Section>

      {/* ── Tips ── */}
      <Section icon="💡" title="เคล็ดลับการใช้งาน" color="#0891b2">
        <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.8 }}>
          <div>• <strong>Hover ที่เซลสีเหลือง</strong> เพื่อดูค่าเดิมก่อนเปลี่ยนแปลง</div>
          <div>• <strong>Column Chooser</strong> (ปุ่มมุมขวาบน DataGrid) ใช้ซ่อน/แสดง column ที่ต้องการ</div>
          <div>• <strong>การ filter</strong> ทำได้ทั้งจาก Filter Row ใต้ header และ Header Filter (กดที่ ≡ ข้าง header)</div>
          <div>• <strong>Flow การทำงานแนะนำ:</strong></div>
          <div style={{ marginLeft: 16, background: "#f8fafc", borderRadius: 6, padding: "8px 12px", marginTop: 4, fontSize: 12 }}>
            Search เปรียบเทียบ Snapshot → ตรวจสอบ NEW/CHANGED → บันทึก Snapshot ใหม่ → ส่ง FMC
          </div>
        </div>
      </Section>

    </div>
  );
}

// ─── Modal ─────────────────────────────────────────────────────────────────────
function GuideModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
              📋 คู่มือการใช้งาน
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              US Shipment · Ocean Freight Comparison
            </div>
          </div>
          <button style={S.closeBtn} onClick={onClose} aria-label="ปิด">✕</button>
        </div>
        <GuideContent />
      </div>
    </div>
  );
}

// ─── Inline panel ──────────────────────────────────────────────────────────────
function GuidePanel() {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, fontFamily: "'Noto Sans Thai','DM Sans',sans-serif" }}>
      <div style={{ padding: "14px 20px 10px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>📋</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>คู่มือการใช้งาน</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>US Shipment · Ocean Freight Comparison</div>
        </div>
      </div>
      <GuideContent />
    </div>
  );
}

// ─── Trigger Button ────────────────────────────────────────────────────────────
function GuideTriggerButton({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
      padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#475569",
      cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
      fontFamily: "'Noto Sans Thai','DM Sans',sans-serif",
    }}>
      <span>📋</span> คู่มือการใช้งาน
    </button>
  );
}

// ─── Main Export ───────────────────────────────────────────────────────────────
export function FMCUserGuide({ mode = "modal" }) {
  const [open, setOpen] = useState(false);
  if (mode === "panel") return <GuidePanel />;
  return (
    <>
      <GuideTriggerButton onClick={() => setOpen(true)} />
      <GuideModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default FMCUserGuide;
